import React from 'react';
import { Card, Empty, Progress, Space, Table, Tag, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetIntegrityLlmCostQuery } from '../../../services/nestjs-base-api';

const { Text, Title } = Typography;

const ALERT_COLOR: Record<string, string> = {
    ok: 'green',
    approaching: 'orange',
    exhausted: 'red',
};

interface LlmCostPanelProps {
    embedded?: boolean; // when true, render without the outer Card title
}

export const LlmCostPanel: React.FC<LlmCostPanelProps> = ({ embedded }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const { data, isLoading } = useGetIntegrityLlmCostQuery({
        jwtToken: user?.jwtToken || '',
    });

    const surveys = data?.surveys ?? [];
    const totalSpentUsd: number = data?.totalSpentUsd ?? 0;
    const defaultCapUsd: number = data?.defaultCapUsd ?? 0;

    const body = surveys.length === 0 ? (
        <Empty description={<FormattedMessage id="responseIntegrity.llmCost.empty" />} />
    ) : (
        <Table
            rowKey="surveyId"
            dataSource={surveys}
            pagination={false}
            size="small"
            loading={isLoading}
            columns={[
                {
                    title: <FormattedMessage id="responseIntegrity.llmCost.col.survey" />,
                    dataIndex: 'surveyName',
                    render: (name: string | null, row: any) =>
                        name || `Survey #${row.surveyId}`,
                },
                {
                    title: <FormattedMessage id="responseIntegrity.llmCost.col.spent" />,
                    dataIndex: 'spentUsd',
                    width: 120,
                    align: 'right',
                    render: (v: number) => v.toFixed(4),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.llmCost.col.cap" />,
                    dataIndex: 'capUsd',
                    width: 120,
                    align: 'right',
                    render: (v: number) => v.toFixed(2),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.llmCost.col.utilisation" />,
                    dataIndex: 'utilisation',
                    width: 200,
                    render: (v: number) => (
                        <Progress
                            percent={Math.min(100, Math.max(0, v * 100))}
                            size="small"
                            status={v >= 1 ? 'exception' : v >= 0.8 ? 'active' : 'normal'}
                        />
                    ),
                },
                {
                    title: <FormattedMessage id="responseIntegrity.llmCost.col.alertState" />,
                    dataIndex: 'alertState',
                    width: 140,
                    render: (s: string) => (
                        <Tag color={ALERT_COLOR[s] ?? 'default'} style={{ margin: 0 }}>
                            <FormattedMessage
                                id={`responseIntegrity.llmCost.alertState.${s}`}
                            />
                        </Tag>
                    ),
                },
            ]}
        />
    );

    if (embedded) {
        return (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text type="secondary">
                    Total: ${totalSpentUsd.toFixed(4)} · Default cap: $
                    {defaultCapUsd.toFixed(2)}/survey
                </Text>
                {body}
            </Space>
        );
    }

    return (
        <Card
            title={
                <Space direction="vertical" size={0}>
                    <Title level={5} style={{ margin: 0 }}>
                        <FormattedMessage id="responseIntegrity.llmCost.title" />
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <FormattedMessage id="responseIntegrity.llmCost.subtitle" />
                    </Text>
                </Space>
            }
            extra={
                <Text type="secondary">
                    Total: ${totalSpentUsd.toFixed(4)} · Default cap: $
                    {defaultCapUsd.toFixed(2)}/survey
                </Text>
            }
        >
            {body}
        </Card>
    );
};
